import { ChevronDown, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useCategories } from "../context/CategoriesContext";
import { Category } from "../types/Category";

const HeaderMenu: React.FC = () => {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { t, i18n } = useTranslation();

  let timeoutId: NodeJS.Timeout;

  const changeLanguage = (lang: "en" | "ar") => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  };

  const handleNavigation = (route: string) => {
    // console.log("handle navigation");
    navigate(`/${route}`);
    if (isDrawerOpen) {
      setIsDrawerOpen(false);
    }
  };

  const handleProfileNavigation = (route: string) => {
    navigate(`/${route}`);
    if (isDrawerOpen) {
      setIsDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsDrawerOpen(false);
    navigate("/");
  };

  const getUserDisplayName = () => {
    if (user?.firstName) return user.firstName;
    if (user?.identifier) return user.identifier.split("@")[0];
    return "User";
  };

  const handleCategoryClick = (category: Category) => {
    if (category?.subcategories?.length) {
      navigate(`/category/${category.id}`);
    } else {
      navigate(`/products?categoryId=${category.id}`);
    }
    setIsProductsOpen(false);
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [navigate]);

  return (
    <div>
      {/* Desktop Navigation Menu */}
      <nav className="hidden lg:block bg-gradient-to-r from-[#f2e6db] via-[#f2e6db] to-[#f2e6db] border-t-2 border-[#d4c9ba] px-6 py-6 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-12">
          <div
            className="relative text-black text-base font-semibold hover:text-gray-700 transition-all duration-300 group py-2 cursor-pointer"
            onClick={() => handleNavigation("home")}
          >
            <span className="relative z-10">{t("Home")}</span>
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
          </div>

          {/* Dropdown Menu */}
          <div
            className="relative group"
            onMouseEnter={() => {
              clearTimeout(timeoutId);
              setIsProductsOpen(true);
            }}
            onMouseLeave={() => {
              timeoutId = setTimeout(() => setIsProductsOpen(false), 400); // 200ms delay
            }}
          >
            <button className="relative text-black text-base font-semibold hover:text-gray-700 transition-all duration-300 flex items-center gap-2 py-2">
              <span className="relative z-10">{t("All products")}</span>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-300 ${
                  isProductsOpen ? "rotate-180" : ""
                }`}
                style={{ transformOrigin: "center" }}
              />
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
            </button>

            {/* Dropdown Content */}
            {isProductsOpen && (
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 pt-2 z-50">
                <div className="w-64 bg-white rounded-xl shadow-2xl py-3 border border-gray-100">
                  {categories?.length &&
                    categories.map((category, index) => (
                      <div
                        key={category.id}
                        className="block px-6 py-3 text-base text-gray-800 hover:bg-gradient-to-r hover:from-[#f2e6db] hover:to-[#f2e6db] transition-all duration-200 border-l-4 border-transparent hover:border-black"
                        style={{ animationDelay: `${index * 50}ms` }}
                        onClick={() => handleCategoryClick(category)}
                      >
                        {(i18n.language === "ar"
                          ? category.name?.ar
                          : category.name?.en) || t("No Category")}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          <div
            className="relative text-black text-base font-semibold hover:text-gray-700 transition-all duration-300 group py-2 cursor-pointer"
            onClick={() => handleNavigation("about")}
          >
            <span className="relative z-10">{t("About us")}</span>
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
          </div>

          <div
            className="relative text-black text-base font-semibold hover:text-gray-700 transition-all duration-300 group py-2 cursor-pointer"
            onClick={() => handleNavigation("contact")}
          >
            <span className="relative z-10">{t("Contact us")}</span>
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
          </div>

          <div
            className="relative text-black text-base font-semibold hover:text-gray-700 transition-all duration-300 group py-2 cursor-pointer"
            onClick={() => handleNavigation("privacy-policy")}
          >
            <span className="relative z-10">
              {t("Return and exchange policy")}
            </span>
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {isDrawerOpen && (
        <>
          {/* Overlay */}
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsDrawerOpen(false)}
          />

          {/* Drawer */}
          <div className="lg:hidden fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 overflow-y-auto">
            <div className="p-6">
              {/* Close Button */}
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Profile Section in Mobile */}
              <div className="mt-12 mb-6 pb-4 border-b border-gray-200">
                {!isAuthenticated ? (
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleProfileNavigation("signin")}
                      className="flex-1 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => handleProfileNavigation("signin")}
                      className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Sign Up
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-[#0d223f] rounded-full p-3">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-800">
                          Hi, {getUserDisplayName()}
                        </p>
                        <p className="text-sm text-gray-600">Welcome back!</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() =>
                          handleProfileNavigation("account/profile")
                        }
                        className="text-left px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {t("My Profile")}
                      </button>
                      <button
                        onClick={() =>
                          handleProfileNavigation("account/orders")
                        }
                        className="text-left px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {t("Orders")}
                      </button>
                      <button
                        onClick={() =>
                          handleProfileNavigation("account/returns")
                        }
                        className="text-left px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {t("Returns and Exchanges")}
                      </button>
                      <button
                        onClick={() =>
                          handleProfileNavigation("account/address")
                        }
                        className="text-left px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {t("Address")}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Drawer Navigation */}
              <nav className="flex flex-col gap-4">
                <div
                  className="text-black text-lg py-2 border-b border-gray-200 hover:text-gray-700 transition-colors cursor-pointer"
                  onClick={() => handleNavigation("home")}
                >
                  {t("Home")}
                </div>
                <div
                  className="text-black text-lg py-2 border-b border-gray-200 hover:text-gray-700 transition-colors cursor-pointer"
                  onClick={() => handleNavigation("about")}
                >
                  {t("About us")}
                </div>

                {/* Mobile Products Accordion */}
                <div className="border-b border-gray-200">
                  <button
                    onClick={() => setIsProductsOpen(!isProductsOpen)}
                    className="w-full text-black text-lg py-2 flex items-center justify-between hover:text-gray-700 transition-colors"
                  >
                    {t("All products")}
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${
                        isProductsOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isProductsOpen && (
                    <div className="pl-4 pb-2 flex flex-col gap-2">
                      {categories?.length > 0 &&
                        categories.map((category) => (
                          <div
                            key={category.id}
                            className="text-gray-700 text-base py-1 hover:text-black transition-colors"
                            onClick={() => handleCategoryClick(category)}
                          >
                            {category.name.en}
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                <div
                  className="text-black text-lg py-2 border-b border-gray-200 hover:text-gray-700 transition-colors cursor-pointer"
                  onClick={() => handleNavigation("contact")}
                >
                  {t("Contacts us")}
                </div>
                <div
                  className="text-black text-lg py-2 border-b border-gray-200 hover:text-gray-700 transition-colors cursor-pointer"
                  onClick={() => handleNavigation("privacy-policy")}
                >
                  {t("Return and exchange policy")}
                </div>

                {isAuthenticated && (
                  <button
                    onClick={handleLogout}
                    className="text-red-600 text-lg py-2 border-b border-gray-200 hover:text-red-700 transition-colors text-left"
                  >
                    {t("Logout")}
                  </button>
                )}
              </nav>

              {/* Language Selector (Mobile only, at bottom) */}
              <div className="mt-8 pt-4 px-6">
                <label className="block text-gray-600 text-sm mb-2">
                  {t("Language")}
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                  value={i18n.language}
                  onChange={(e) =>
                    changeLanguage(e.target.value as "en" | "ar")
                  }
                >
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HeaderMenu;
